import type { Permission } from '@almacen/shared';
import { Center, Loader } from '@mantine/core';
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router';
import { RequireAuth, RequirePermission } from './auth/guards';
import { AppLayout } from './components/layout/AppLayout';
import { NotFoundPage } from './components/NotFoundPage';
import { LoginPage } from './features/auth/LoginPage';
import { DashboardPage } from './features/dashboard/DashboardPage';

/** Carga diferida por pantalla: el navegador solo descarga el código de lo que se usa. */
function lazyPage<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M) {
  const Page = lazy(async () => ({ default: (await load())[name] as ComponentType }));
  return (
    <Suspense
      fallback={
        <Center py="xl">
          <Loader />
        </Center>
      }
    >
      <Page />
    </Suspense>
  );
}

function guarded(permission: Permission, element: ReactNode) {
  return <RequirePermission permission={permission}>{element}</RequirePermission>;
}

function printPage(permission: Permission, element: ReactNode) {
  return <RequireAuth>{guarded(permission, element)}</RequireAuth>;
}

const page = {
  search: () => lazyPage(() => import('./features/search/SearchPage'), 'SearchPage'),
  entry: () => lazyPage(() => import('./features/entry/EntryPage'), 'EntryPage'),
  locate: () => lazyPage(() => import('./features/locate/LocatePage'), 'LocatePage'),
  map: () => lazyPage(() => import('./features/map/MapPage'), 'MapPage'),
  outboundList: () =>
    lazyPage(() => import('./features/outbound/OutboundListPage'), 'OutboundListPage'),
  outboundNew: () =>
    lazyPage(() => import('./features/outbound/NewOutboundPage'), 'NewOutboundPage'),
  outboundOrder: () =>
    lazyPage(() => import('./features/outbound/OutboundOrderPage'), 'OutboundOrderPage'),
  inventory: () => lazyPage(() => import('./features/inventory/InventoryPage'), 'InventoryPage'),
  history: () => lazyPage(() => import('./features/history/HistoryPage'), 'HistoryPage'),
  pallet: () => lazyPage(() => import('./features/pallets/PalletDetailPage'), 'PalletDetailPage'),
  clients: () => lazyPage(() => import('./features/catalog/ClientsPage'), 'ClientsPage'),
  clientForm: () => lazyPage(() => import('./features/catalog/ClientFormPage'), 'ClientFormPage'),
  products: () => lazyPage(() => import('./features/catalog/ProductsPage'), 'ProductsPage'),
  locations: () =>
    lazyPage(() => import('./features/warehouse/LocationsConfigPage'), 'LocationsConfigPage'),
  rates: () => lazyPage(() => import('./features/billing/RatesPage'), 'RatesPage'),
  invoices: () => lazyPage(() => import('./features/billing/InvoicesPage'), 'InvoicesPage'),
  invoiceNew: () => lazyPage(() => import('./features/billing/NewInvoicePage'), 'NewInvoicePage'),
  invoice: () =>
    lazyPage(() => import('./features/billing/InvoiceDetailPage'), 'InvoiceDetailPage'),
  users: () => lazyPage(() => import('./features/admin/UsersPage'), 'UsersPage'),
  settings: () => lazyPage(() => import('./features/admin/SettingsPage'), 'SettingsPage'),
  audit: () => lazyPage(() => import('./features/admin/AuditPage'), 'AuditPage'),
  palletLabels: () =>
    lazyPage(() => import('./features/labels/PalletLabelsPage'), 'PalletLabelsPage'),
  locationLabels: () =>
    lazyPage(() => import('./features/labels/LocationLabelsPage'), 'LocationLabelsPage'),
};

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Páginas de impresión: sin menú ni cabecera.
  { path: '/imprimir/palets', element: printPage('pallets.read', page.palletLabels()) },
  { path: '/imprimir/ubicaciones', element: printPage('locations.read', page.locationLabels()) },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'buscar', element: guarded('pallets.read', page.search()) },
      { path: 'entrada', element: guarded('pallets.entry', page.entry()) },
      { path: 'ubicar', element: guarded('pallets.locate', page.locate()) },
      { path: 'mapa', element: guarded('locations.read', page.map()) },
      { path: 'salidas', element: guarded('pallets.read', page.outboundList()) },
      { path: 'salidas/nueva', element: guarded('outbound.manage', page.outboundNew()) },
      { path: 'salidas/:id', element: guarded('pallets.read', page.outboundOrder()) },
      { path: 'inventario', element: guarded('pallets.read', page.inventory()) },
      { path: 'historico', element: guarded('pallets.read', page.history()) },
      { path: 'palets/:id', element: guarded('pallets.read', page.pallet()) },
      { path: 'clientes', element: guarded('clients.read', page.clients()) },
      { path: 'clientes/nuevo', element: guarded('clients.manage', page.clientForm()) },
      { path: 'clientes/:id', element: guarded('clients.read', page.clientForm()) },
      { path: 'mercancias', element: guarded('products.read', page.products()) },
      { path: 'ubicaciones', element: guarded('locations.manage', page.locations()) },
      { path: 'tarifas', element: guarded('rates.read', page.rates()) },
      { path: 'liquidaciones', element: guarded('billing.read', page.invoices()) },
      { path: 'liquidaciones/nueva', element: guarded('billing.manage', page.invoiceNew()) },
      { path: 'liquidaciones/:id', element: guarded('billing.read', page.invoice()) },
      { path: 'usuarios', element: guarded('users.manage', page.users()) },
      { path: 'configuracion', element: guarded('settings.manage', page.settings()) },
      { path: 'auditoria', element: guarded('audit.read', page.audit()) },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
